'use client';

/**
 * ADS PARITY ROOM — pixel port of Control Room v6 `adsx` (Design round 3,
 * uploads/design_v2/deploy/Control Room v6.dc.html §7) wired to the REAL APIs.
 * The prototype's axDB() mock is replaced 1:1 by:
 *   GET  /api/ads/accounts                    — account selector (name · lastScore)
 *   GET  /api/ads/campaigns/[id]              — campaign selector
 *   GET  /api/ads/audit/[id]?window           — score/parts/KPIs/findings/recs/benchmarks/notes
 *   POST /api/ads/audit/[id]?window           — ⟳ REFRESH from Meta Graph API
 *   GET  /api/ads/insights/[id]?window        — smart insights + timeseries + breakdowns
 *   GET  /api/ads/tracker/[id]?month=         — tracker daily rows
 * Stale-response guard (auditSeq/bundleSeq) preserved from classic — only the
 * newest request writes the screen. Window + checklist persist in localStorage
 * (bcc.ads.window / bcc.ads.checklist) exactly like the prototype.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { Audit, InsightsBundle, BreakdownRow, EntityRow } from '@/lib/ads-types';
import { AdsAssistant } from '@/components/ads/AdsAssistant';
import type { BusinessProject } from '@/lib/business-projects';

/* ── v6 constants (README §2-§4, verbatim) ─────────────────────────────── */
const MONO = 'Spline Sans Mono, monospace';
const SAIRA = 'Saira, sans-serif';
const scC = (v: number) => (v >= 75 ? '#5df2b4' : v >= 55 ? '#ffc36b' : '#ff7d8a');
const SEV: Record<string, [string, string]> = {
  critical: ['#ff7d8a', '!'], warning: ['#ffc36b', '!'], info: ['#9ef0ff', 'i'], positive: ['#5df2b4', '✓'],
};
const PRI: Record<string, string> = { high: '#ff7d8a', medium: '#ffc36b', low: '#7fa9bd' };
const CKS = ['Choose account', 'Choose campaign', 'Custom range', 'Refresh from Meta', 'Audit score', 'Score breakdown',
  'Spend · Impr · Reach · Clicks', 'CTR · CPC · CPM · Freq', 'Conversions', 'Smart insights', 'Findings',
  'Recommendations', 'Benchmarks', 'All on one screen', 'Trends', 'Audience', 'Delivery', 'Campaign tables', 'Tracker', 'Assistant'];
const TABS = ['overview', 'trends', 'audience', 'delivery', 'campaigns', 'tracker', 'assistant'] as const;
type Tab = typeof TABS[number];
const RANGE_OPTS: [string, string][] = [['last_7d', 'Last 7 days'], ['last_14d', 'Last 14 days'], ['last_30d', 'Last 30 days'], ['last_90d', 'Last 90 days'], ['custom', 'Custom…']];
const RANGE_LABEL: Record<string, string> = { last_7d: 'LAST 7 DAYS', last_14d: 'LAST 14 DAYS', last_30d: 'LAST 30 DAYS', last_90d: 'LAST 90 DAYS' };

const fmtN = (n: number) => (n >= 10000 ? `${Math.round(n / 100) / 10}K` : Math.round(n).toLocaleString('en-US'));
const fmtMoney = (n: number, cur: string) => `${cur === 'EUR' ? '€' : cur === 'USD' ? '$' : cur === 'CAD' ? 'CA$' : cur + ' '}${n >= 10000 ? fmtN(n) : n.toFixed(2)}`;
const isoDaysAgo = (d: number) => new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);
const trendChip = (v: number | undefined) => (v == null || !isFinite(v) || v === 0 ? '—' : `${v > 0 ? '↑' : '↓'}${Math.abs(v).toFixed(0)}%`);

interface AccountRow { id: string; name: string; currency?: string; lastScore?: number | null; lastAudit?: string | null }

/* slab geometry from the prototype, shifted to a 0,0-origin canvas (x−120, y−238) */
const GEOM: Record<string, [number, number, number, number, Tab[]]> = {
  ring: [0, 0, 282, 252, ['overview']], brk: [294, 0, 436, 252, ['overview']], kpi: [742, 0, 938, 252, ['overview']],
  conv: [0, 264, 282, 236, ['overview']], ins: [294, 264, 436, 236, ['overview']], fnd: [742, 264, 452, 236, ['overview']],
  rec: [1206, 264, 474, 236, ['overview']], bmk: [0, 512, 726, 180, ['overview']], note: [738, 512, 942, 180, ['overview']],
  g1: [0, 0, 546, 300, ['trends', 'audience', 'delivery']], g2: [558, 0, 546, 300, ['trends', 'audience', 'delivery']], g3: [1116, 0, 564, 300, ['trends', 'audience', 'delivery']],
  t1: [0, 0, 1680, 212, ['campaigns']], t2: [0, 226, 1680, 212, ['campaigns']], t3: [0, 452, 1680, 240, ['campaigns']],
  trk: [0, 0, 1680, 300, ['tracker']], ast: [0, 0, 1680, 520, ['assistant']],
};

export function AdsParitySurface({ project }: { project?: BusinessProject | null }) {
  const onlyAccountId = project?.adsAccountId;
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [selected, setSelected] = useState('');
  const [campaigns, setCampaigns] = useState<Array<{ id: string; name: string; status: string }>>([]);
  const [campaign, setCampaign] = useState('__ALL__');
  const [audit, setAudit] = useState<Audit | null>(null);
  const [bundle, setBundle] = useState<InsightsBundle | null>(null);
  const [tracker, setTracker] = useState<any>(null);
  const [noAudit, setNoAudit] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [liveRefresh, setLiveRefresh] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [range, setRange] = useState('last_30d');
  const [since, setSince] = useState(''); const [until, setUntil] = useState('');
  const [dd, setDD] = useState<string | null>(null);
  const [pull, setPull] = useState<string | null>(null);
  const [offs, setOffs] = useState<Record<string, { x: number; y: number }>>({});
  const [grab, setGrab] = useState<{ id: string; ox: number; oy: number } | null>(null);
  const [check, setCheck] = useState<Record<number, boolean>>({});
  const [checkOpen, setCheckOpen] = useState(false);
  const auditSeq = useRef(0); const bundleSeq = useRef(0);
  const rootRef = useRef<HTMLDivElement>(null);

  /* window + checklist persistence (bcc.ads.window / bcc.ads.checklist) */
  useEffect(() => {
    try {
      const w = JSON.parse(localStorage.getItem('bcc.ads.window') || 'null');
      if (w?.range) { setRange(w.range); setSince(w.since || ''); setUntil(w.until || ''); }
      const c = JSON.parse(localStorage.getItem('bcc.ads.checklist') || 'null');
      if (c) setCheck(c);
    } catch { /* fresh */ }
  }, []);
  const persistWin = (r: string, s: string, u: string) => { try { localStorage.setItem('bcc.ads.window', JSON.stringify({ range: r, since: s, until: u })); } catch { } };

  const isCustom = range === 'custom';
  const customReady = isCustom && !!since && !!until && since <= until;
  const winQuery = (isCustom ? `since=${since}&until=${until}` : `range=${range}`) + (campaign !== '__ALL__' ? `&campaign=${campaign}` : '');
  const winLabel = isCustom ? `${since || '…'} → ${until || '…'}` : (RANGE_LABEL[range] || range);

  useEffect(() => {
    fetch('/api/ads/accounts').then(r => r.json()).then(d => {
      let accs: AccountRow[] = d.accounts || [];
      if (onlyAccountId) accs = accs.filter(a => a.id === onlyAccountId);
      setAccounts(accs); setLiveRefresh(!!d.liveRefresh);
      const first = accs.find(a => a.lastAudit) || accs[0];
      if (first) setSelected(first.id);
    }).catch(() => { });
  }, [onlyAccountId]);

  useEffect(() => {
    if (!selected) return;
    setCampaign('__ALL__');
    fetch(`/api/ads/campaigns/${selected}`).then(r => r.json()).then(d => setCampaigns(d.campaigns || [])).catch(() => setCampaigns([]));
  }, [selected]);

  const loadAudit = useCallback((id: string, qs: string) => {
    const seq = ++auditSeq.current;
    fetch(`/api/ads/audit/${id}?${qs}`).then(async r => ({ ok: r.ok, status: r.status, body: await r.json() })).then(({ ok, status, body }) => {
      if (seq !== auditSeq.current) return;
      if (ok) { setAudit(body.audit); setNoAudit(false); } else if (status === 404) { setAudit(null); setNoAudit(true); }
    }).catch(() => { });
  }, []);
  const loadBundle = useCallback((id: string, qs: string) => {
    const seq = ++bundleSeq.current;
    setBundle(null);
    fetch(`/api/ads/insights/${id}?${qs}`).then(async r => ({ ok: r.ok, body: await r.json() })).then(({ ok, body }) => {
      if (seq === bundleSeq.current && ok) setBundle(body.bundle);
    }).catch(() => { });
  }, []);

  useEffect(() => {
    if (!selected || (isCustom && !customReady)) return;
    loadAudit(selected, winQuery); loadBundle(selected, winQuery);
  }, [selected, winQuery, isCustom, customReady, loadAudit, loadBundle]);

  useEffect(() => {
    if (!selected || tab !== 'tracker') return;
    fetch(`/api/ads/tracker/${selected}?month=${new Date().toISOString().slice(0, 7)}`).then(r => r.json()).then(setTracker).catch(() => setTracker(null));
  }, [selected, tab]);

  const refresh = () => {
    if (!selected || refreshing || (isCustom && !customReady)) return;
    setRefreshing(true);
    const seq = ++auditSeq.current; ++bundleSeq.current;
    fetch(`/api/ads/audit/${selected}?${winQuery}`, { method: 'POST' }).then(async r => ({ ok: r.ok, body: await r.json() })).then(({ ok, body }) => {
      if (seq !== auditSeq.current) return;
      if (ok) {
        setAudit(body.audit); setNoAudit(false); loadBundle(selected, winQuery);
        setToast(`✓ REFRESHED FROM META · ${winLabel}`); setTimeout(() => setToast(null), 3600);
      } else { setToast(`✕ ${body.message || 'REFRESH FAILED'}`); setTimeout(() => setToast(null), 5000); }
    }).catch(e => { setToast(`✕ ${e.message}`); setTimeout(() => setToast(null), 5000); }).finally(() => setRefreshing(false));
  };

  /* grab-and-move (⠿) — v6 §4 */
  useEffect(() => {
    if (!grab) return;
    const move = (e: PointerEvent) => setOffs(o => ({ ...o, [grab.id]: { x: e.clientX - grab.ox, y: e.clientY - grab.oy } }));
    const up = () => setGrab(null);
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, [grab]);

  const acct = accounts.find(a => a.id === selected);
  const cur = audit?.account.currency || acct?.currency || '';
  const hasAudit = !!audit;
  const score = audit ? Math.round(audit.score) : 0;
  const scoreCol = scC(score);
  const isLive = audit?.source === 'graph-live';
  const campName = campaign === '__ALL__' ? null : campaigns.find(c => c.id === campaign)?.name;
  const done = CKS.filter((_, i) => check[i]).length;

  /* ── v6 style primitives ─────────────────────────────────────────────── */
  const slab = (id: string): { s: CSSProperties; on: boolean; grabH: (e: React.PointerEvent) => void; pullH: () => void } => {
    const [x, y, w, h, tabs] = GEOM[id];
    const on = tabs.includes(tab);
    const off = offs[id] || { x: 0, y: 0 };
    const g = grab?.id === id; const pulled = pull === id; const other = !!pull && !pulled;
    const k = pulled ? Math.min(1660 / w, 690 / h, 2.3) : 1;
    const tx = pulled ? 840 - (x + w / 2) : off.x;
    const ty = pulled ? 340 - (y + h / 2) : off.y;
    return {
      on,
      s: {
        position: 'absolute', left: x, top: y, width: w, height: h, boxSizing: 'border-box', borderRadius: 14, padding: 14,
        background: 'linear-gradient(170deg,rgba(40,140,180,.08),rgba(8,18,28,.55) 55%)',
        border: `1px solid rgba(125,232,255,${g || pulled ? '.6' : '.24'})`,
        boxShadow: pulled ? '0 60px 140px -30px rgba(0,0,0,.95),0 0 90px -16px rgba(0,240,255,.5)' : g ? '0 34px 90px -20px rgba(0,0,0,.85),0 0 60px -6px rgba(0,240,255,.5)' : '0 0 40px -12px rgba(0,240,255,.18)',
        overflow: 'hidden', zIndex: pulled ? 80 : g ? 62 : 36, opacity: on ? (other ? .15 : 1) : 0,
        pointerEvents: on && !other ? 'auto' : 'none', visibility: on ? 'visible' : 'hidden',
        transform: `translate(${tx}px,${ty}px) scale(${k})`,
        transition: g ? 'box-shadow .2s,border-color .2s' : 'transform .5s cubic-bezier(.22,1.18,.32,1),opacity .4s,box-shadow .3s,border-color .3s,visibility .4s',
      },
      grabH: (e) => { e.stopPropagation(); if (pull) return; const c = offs[id] || { x: 0, y: 0 }; setGrab({ id, ox: e.clientX - c.x, oy: e.clientY - c.y }); setDD(null); },
      pullH: () => { setPull(p => (p === id ? null : id)); setGrab(null); setDD(null); },
    };
  };
  const slabHead = (title: string, id: string, extra?: React.ReactNode) => {
    const sl = slab(id);
    return { sl, head: (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontFamily: MONO, fontSize: 13, letterSpacing: '.22em', color: '#49677a' }}>{title}</span>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {extra}
          <span onPointerDown={sl.grabH} style={{ cursor: 'grab', color: '#3d4654', fontSize: 20, userSelect: 'none' }} title="move">⠿</span>
          <span onClick={sl.pullH} style={{ cursor: 'pointer', color: '#56788c', fontSize: 20, userSelect: 'none' }} title="pull closer">⤢</span>
        </span>
      </div>
    ) };
  };
  const scanline: CSSProperties = { position: 'absolute', inset: 0, pointerEvents: 'none', background: 'repeating-linear-gradient(180deg, rgba(140,235,255,.05) 0 1px, transparent 1px 3px)', mixBlendMode: 'screen', opacity: .5 };
  const ddBtn = (id: string, label: string): { open: boolean; btnS: CSSProperties; menuS: CSSProperties; toggle: () => void; label: string } => {
    const open = dd === id;
    return {
      open, label: `${label}  ▾`,
      toggle: () => { setDD(open ? null : id); setPull(null); },
      btnS: { padding: '8px 15px', borderRadius: 999, border: `1px solid rgba(125,232,255,${open ? '.6' : '.25'})`, background: open ? 'rgba(0,240,255,.08)' : 'rgba(3,8,13,.5)', color: '#bfeaff', fontFamily: MONO, fontSize: 17, letterSpacing: '.14em', cursor: 'pointer', transition: 'all .25s', whiteSpace: 'nowrap' },
      menuS: { position: 'absolute', left: 0, top: 38, minWidth: 268, borderRadius: 12, border: '1px solid rgba(125,232,255,.35)', background: 'linear-gradient(170deg,rgba(40,140,180,.12),rgba(4,10,18,.97) 55%)', boxShadow: '0 30px 80px -20px rgba(0,0,0,.9),0 0 50px -12px rgba(0,240,255,.35)', padding: 8, zIndex: 92, opacity: open ? 1 : 0, transform: open ? 'translateY(0)' : 'translateY(-6px)', pointerEvents: open ? 'auto' : 'none', visibility: open ? 'visible' : 'hidden', transition: 'all .25s' },
    };
  };
  const mrow = (on: boolean): CSSProperties => ({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '9px 12px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${on ? 'rgba(158,240,255,.4)' : 'transparent'}`, background: on ? 'rgba(0,240,255,.07)' : 'transparent', transition: 'all .2s' });
  const dateS: CSSProperties = { background: 'rgba(8,18,28,.6)', border: '1px solid rgba(125,232,255,.28)', borderRadius: 9, padding: '7px 10px', color: '#bfeaff', fontFamily: MONO, fontSize: 18, outline: 'none', colorScheme: 'dark' };

  /* breakdown rows → % of spend bars (real share) */
  const shareRows = (rows: BreakdownRow[] | undefined, max = 8, mult = 2.2) => {
    if (!rows?.length) return [];
    const tot = rows.reduce((s, r) => s + (r.spend || 0), 0) || 1;
    return [...rows].sort((a, b) => (b.spend || 0) - (a.spend || 0)).slice(0, max).map(r => {
      const pc = Math.round(((r.spend || 0) / tot) * 100);
      return { l: r.key.toUpperCase(), v: `${pc}%`, w: Math.min(100, pc * mult) };
    });
  };
  /* timeseries → v6 chart path, viewBox 0 0 320 90 */
  const chartPath = (key: 'spend' | 'clicks' | 'ctr') => {
    const ts = bundle?.timeseries || [];
    if (ts.length < 2) return '';
    const vals = ts.map(p => (p as any)[key] || 0);
    const mx = Math.max(...vals, 0.0001);
    return vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i / (vals.length - 1) * 320).toFixed(0)},${(84 - (v / mx) * 74).toFixed(0)}`).join(' ');
  };
  const barRows = (rows: { l: string; v: string; w: number }[]) => rows.map((r, i) => (
    <div key={i} style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontFamily: SAIRA, fontWeight: 300, fontSize: 23, color: '#c4d8e4' }}>{r.l}</span>
        <span style={{ fontFamily: MONO, fontSize: 19, color: '#aef0ff' }}>{r.v}</span>
      </div>
      <div style={{ height: 2, marginTop: 4, width: `${r.w}%`, background: 'linear-gradient(90deg,#9ef0ff,rgba(125,232,255,.06))' }} />
    </div>
  ));
  const entityTable = (title: string, rows: EntityRow[], id: string) => {
    const { sl, head } = slabHead(`${title.toUpperCase()} · ${rows.length}`, id);
    const W = [22, 9, 10, 11, 10, 8, 8, 11, 11];
    const H = ['NAME', 'STATUS', 'SPEND', 'IMPRESSIONS', 'CLICKS', 'CTR', 'CPC', 'CONV (PRIMARY)', 'COST / RESULT'];
    return (
      <div style={sl.s}>
        <div style={scanline} />{head}
        <div style={{ display: 'flex', marginTop: 6 }}>{H.map((h, i) => <span key={h} style={{ flex: `0 0 ${W[i]}%`, fontFamily: MONO, fontSize: 13, letterSpacing: '.18em', color: '#49677a', whiteSpace: 'nowrap', overflow: 'hidden' }}>{h}</span>)}</div>
        <div style={{ overflowY: 'auto', maxHeight: GEOM[id][3] - 70 }}>
          {rows.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', padding: '7px 0', borderTop: '1px solid rgba(125,232,255,.06)' }}>
              <span style={{ flex: `0 0 ${W[0]}%`, fontFamily: SAIRA, fontWeight: 400, fontSize: 23, color: '#e9f9ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 8 }}>{r.name}</span>
              {[r.status?.toUpperCase() || '—', fmtMoney(r.spend, cur), fmtN(r.impressions), fmtN(r.clicks), `${r.ctr.toFixed(2)}%`, fmtMoney(r.cpc, cur), r.convCount != null ? String(r.convCount) : '—', r.convCostPer != null ? fmtMoney(r.convCostPer, cur) : '—']
                .map((c, i) => <span key={i} style={{ flex: `0 0 ${W[i + 1]}%`, fontFamily: MONO, fontSize: 17, color: i === 0 ? (c === 'ACTIVE' ? '#5df2b4' : '#ffc36b') : '#aef0ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 8 }}>{c}</span>)}
            </div>
          ))}
          {!rows.length && <div style={{ marginTop: 14, fontFamily: SAIRA, fontWeight: 300, fontSize: 23, color: '#56788c' }}>Nothing in this window.</div>}
        </div>
      </div>
    );
  };

  const ddA = ddBtn('account', `ACCOUNT · ${acct?.name || '—'} · ${acct?.lastScore != null ? Math.round(acct.lastScore) : '—'}`);
  const ddC = ddBtn('campaign', `CAMPAIGN · ${campName ? campName.slice(0, 22) : 'ALL'}`);
  const ddR = ddBtn('range', winLabel);
  const kpis: [string, string, string, boolean][] = audit ? [
    ['SPEND', fmtMoney(audit.totals.spend, cur), trendChip(audit.trend.spend), (audit.trend.spend ?? 0) >= 0],
    ['IMPR.', fmtN(audit.totals.impressions), trendChip(audit.trend.impressions), (audit.trend.impressions ?? 0) >= 0],
    ['REACH', fmtN(audit.totals.reach || 0), trendChip(audit.trend.reach), (audit.trend.reach ?? 0) >= 0],
    ['CLICKS', fmtN(audit.totals.clicks), trendChip(audit.trend.clicks), (audit.trend.clicks ?? 0) >= 0],
    ['CTR', `${audit.totals.ctr.toFixed(2)}%`, trendChip(audit.trend.ctr), (audit.trend.ctr ?? 0) >= 0],
    ['CPC', fmtMoney(audit.totals.cpc, cur), trendChip(audit.trend.cpc), (audit.trend.cpc ?? 0) <= 0],
    ['CPM', fmtMoney(audit.totals.cpm, cur), trendChip(audit.trend.cpm), (audit.trend.cpm ?? 0) <= 0],
    ['FREQ.', (audit.totals.frequency || 0).toFixed(2), trendChip(audit.trend.frequency), (audit.trend.frequency ?? 0) <= 0],
  ] : [];

  const ring = slabHead(`AUDIT SCORE · ${winLabel}`, 'ring');
  const brk = slabHead('SCORE BREAKDOWN · 6 WEIGHTED PARTS', 'brk');
  const kpi = slabHead(`KPIS · ${winLabel} VS PRIOR${campName ? ` · SCOPED: ${campName.toUpperCase()}` : ''}`, 'kpi');
  const conv = slabHead('CONVERSIONS · TRACKED RESULTS', 'conv');
  const ins = slabHead('SMART INSIGHTS · FROM BREAKDOWNS', 'ins');
  const fnd = slabHead('FINDINGS', 'fnd', <span style={{ padding: '1px 8px', borderRadius: 999, border: '1px solid rgba(125,232,255,.3)', color: '#9ef0ff', fontFamily: MONO, fontSize: 15 }}>{audit?.findings.length ?? 0}</span>);
  const rec = slabHead('RECOMMENDATIONS', 'rec', <span style={{ padding: '1px 8px', borderRadius: 999, border: '1px solid rgba(125,232,255,.3)', color: '#9ef0ff', fontFamily: MONO, fontSize: 15 }}>{audit?.recommendations.length ?? 0}</span>);
  const bmk = slabHead('BENCHMARKS · GENERAL REFERENCE', 'bmk');
  const note = slabHead('NOTES & CAVEATS', 'note');
  const trk = slabHead(`TRACKER · DAILY ROWS · ${winLabel}${campName ? ` · ${campName.toUpperCase()}` : ''}`, 'trk');
  const ast = slabHead(`ASSISTANT · SCOPED TO ${(acct?.name || '').toUpperCase()} · ${winLabel} · WORKS WITHOUT SAVED AUDIT`, 'ast');
  const charts: [string, string, 'spend' | 'clicks' | 'ctr'][] = [['SPEND · DAILY', 'g1', 'spend'], ['CLICKS · DAILY', 'g2', 'clicks'], ['CTR · DAILY', 'g3', 'ctr']];
  const audRows = [['BY AGE', 'g1', shareRows(bundle?.byAge, 6)], ['BY GENDER', 'g2', shareRows(bundle?.byGender, 4, 1.5)], ['BY COUNTRY · TOP 15', 'g3', shareRows(bundle?.byCountry, 15)]] as const;
  const delRows = [['BY PLATFORM', 'g1', shareRows(bundle?.byPlatform, 6, 1.6)], ['BY DEVICE', 'g2', shareRows(bundle?.byDevice, 4, 1)], ['BY PLACEMENT POSITION', 'g3', shareRows(bundle?.byPosition, 8)]] as const;

  return (
    <div ref={rootRef} style={{ position: 'relative', width: 1680, minHeight: 720, margin: '0 auto', fontFamily: SAIRA }} onClick={() => dd && setDD(null)}>
      {/* ── header row: account · campaign · range · custom dates · refresh · source · checklist ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 18, position: 'relative', zIndex: 92 }}>
        <div style={{ position: 'relative' }}>
          <div style={ddA.btnS} onClick={e => { e.stopPropagation(); ddA.toggle(); }}>{ddA.label}</div>
          <div style={ddA.menuS}>
            {accounts.map(a => (
              <div key={a.id} style={mrow(a.id === selected)} onClick={e => { e.stopPropagation(); setSelected(a.id); setCampaign('__ALL__'); setTab('overview'); setDD(null); setPull(null); setOffs({}); }}>
                <span style={{ fontFamily: SAIRA, fontWeight: 300, fontSize: 24, color: '#e9f9ff' }}>{a.name || a.id}</span>
                <span style={{ fontFamily: MONO, fontSize: 15, letterSpacing: '.12em', color: a.lastScore != null ? scC(Math.round(a.lastScore)) : '#ffc36b' }}>{a.lastScore != null ? `SCORE ${Math.round(a.lastScore)}` : 'NO AUDIT'}</span>
              </div>
            ))}
          </div>
        </div>
        {campaigns.length > 0 && (
          <div style={{ position: 'relative' }}>
            <div style={ddC.btnS} onClick={e => { e.stopPropagation(); ddC.toggle(); }}>{ddC.label}</div>
            <div style={ddC.menuS}>
              {[{ id: '__ALL__', name: 'All campaigns' }, ...campaigns].map(c => (
                <div key={c.id} style={mrow(campaign === c.id)} onClick={e => { e.stopPropagation(); setCampaign(c.id); setDD(null); }}>
                  <span style={{ fontFamily: SAIRA, fontWeight: 300, fontSize: 24, color: '#e9f9ff' }}>{c.name}</span>
                  <span style={{ fontFamily: MONO, fontSize: 14, letterSpacing: '.12em', color: '#56788c' }}>{c.id === '__ALL__' ? `${campaigns.length} LIVE` : 'SCOPES ALL TABS'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ position: 'relative' }}>
          <div style={ddR.btnS} onClick={e => { e.stopPropagation(); ddR.toggle(); }}>{ddR.label}</div>
          <div style={ddR.menuS}>
            {RANGE_OPTS.map(([v, l]) => (
              <div key={v} style={mrow(range === v)} onClick={e => {
                e.stopPropagation(); setRange(v); setDD(null);
                let s2 = since, u2 = until;
                if (v === 'custom' && !since) { s2 = isoDaysAgo(7); u2 = isoDaysAgo(1); setSince(s2); setUntil(u2); }
                persistWin(v, s2, u2);
              }}>
                <span style={{ fontFamily: SAIRA, fontWeight: 300, fontSize: 24, color: '#e9f9ff' }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
        {isCustom && (<>
          <input type="date" value={since} max={until || isoDaysAgo(1)} onChange={e => { setSince(e.target.value); persistWin(range, e.target.value, until); }} style={dateS} />
          <input type="date" value={until} min={since} max={isoDaysAgo(1)} onChange={e => { setUntil(e.target.value); persistWin(range, since, e.target.value); }} style={dateS} />
        </>)}
        <div onClick={refresh} title={liveRefresh ? 'POST /api/ads/audit — Meta Graph API' : 'Live refresh needs META_ACCESS_TOKEN'}
          style={{ padding: '8px 16px', borderRadius: 999, fontFamily: MONO, fontSize: 17, letterSpacing: '.16em', cursor: 'pointer', transition: 'all .25s', whiteSpace: 'nowrap', ...(refreshing ? { color: '#ffc36b', border: '1px solid rgba(255,195,107,.55)', background: 'rgba(255,195,107,.08)' } : { color: '#04121a', background: 'linear-gradient(180deg,#a8eeff,#5fd4ef)', boxShadow: '0 0 16px rgba(0,240,255,.4)' }) }}>
          {refreshing ? '⟳ PULLING FROM META…' : '⟳ REFRESH'}
        </div>
        <span style={{ fontFamily: MONO, fontSize: 15, letterSpacing: '.18em', color: isLive ? '#5df2b4' : hasAudit ? '#7fa9bd' : '#ffc36b', whiteSpace: 'nowrap' }}>
          {hasAudit ? (isLive ? '● LIVE · GRAPH API' : '◌ SNAPSHOT · MCP') : '— NO AUDIT SAVED'}
        </span>
        <div style={{ flex: 1 }} />
        {/* ◈ DECISION DECK — v6 header chip: jump to the ads one-focus deck */}
        <a href={`/future?surface=ads-deck${project ? `:${project.slug}` : ''}`} style={{ textDecoration: 'none' }}>
          <div style={{ padding: '7px 16px', borderRadius: 999, border: '1px solid rgba(158,240,255,.35)', color: '#9ef0ff', background: 'rgba(3,8,13,.6)', fontFamily: MONO, fontSize: 16, letterSpacing: '.2em', cursor: 'pointer', whiteSpace: 'nowrap' }}>◈ DECISION DECK</div>
        </a>
        <div onClick={e => { e.stopPropagation(); setCheckOpen(o => !o); }}
          style={{ padding: '7px 18px', borderRadius: 999, border: `1px solid ${done === CKS.length ? 'rgba(93,242,180,.55)' : 'rgba(125,232,255,.3)'}`, color: done === CKS.length ? '#5df2b4' : '#9ef0ff', background: 'rgba(3,8,13,.6)', fontFamily: MONO, fontSize: 16, letterSpacing: '.2em', cursor: 'pointer', transition: 'all .25s' }}>
          ☑ ACCEPTANCE · {done}/{CKS.length} TICKED
        </div>
      </div>

      {/* tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, position: 'relative', zIndex: 45 }}>
        {TABS.map(t => (
          <div key={t} onClick={() => { setTab(t); setPull(null); setDD(null); }}
            style={tab === t
              ? { padding: '6px 16px', borderRadius: 999, color: '#04121a', background: 'linear-gradient(180deg,#a8eeff,#5fd4ef)', boxShadow: '0 0 16px rgba(0,240,255,.4)', fontFamily: MONO, fontSize: 16, letterSpacing: '.2em', cursor: 'pointer', transition: 'all .3s' }
              : { padding: '6px 16px', borderRadius: 999, color: '#56788c', border: '1px solid rgba(125,232,255,.16)', fontFamily: MONO, fontSize: 16, letterSpacing: '.2em', cursor: 'pointer', transition: 'all .3s' }}>
            {t.toUpperCase()}
          </div>
        ))}
      </div>

      {/* toast */}
      <div style={{ position: 'absolute', right: 0, top: 52, padding: '9px 16px', borderRadius: 999, border: '1px solid rgba(93,242,180,.5)', background: 'rgba(6,12,19,.9)', fontFamily: MONO, fontSize: 16, letterSpacing: '.14em', color: '#5df2b4', zIndex: 95, opacity: toast ? 1 : 0, transform: toast ? 'translateY(0)' : 'translateY(-8px)', pointerEvents: 'none', transition: 'all .3s' }}>{toast}</div>

      {/* veil for pulled slab */}
      <div onClick={() => setPull(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(1,2,9,.55)', backdropFilter: 'blur(4px)', zIndex: 70, opacity: pull ? 1 : 0, pointerEvents: pull ? 'auto' : 'none', transition: 'opacity .4s' }} />

      {/* ── slab canvas ── */}
      <div style={{ position: 'relative', height: 710 }}>
        {/* OVERVIEW · score ring */}
        <div style={ring.sl.s}><div style={scanline} />{ring.head}
          <div style={{ display: 'grid', placeItems: 'center', marginTop: 6 }}>
            <div style={{ width: 148, height: 148, borderRadius: '50%', display: 'grid', placeItems: 'center', background: hasAudit ? `conic-gradient(${scoreCol} ${score * 3.6}deg, rgba(125,232,255,.1) 0deg)` : 'conic-gradient(rgba(125,232,255,.12) 360deg, rgba(125,232,255,.12) 0deg)', boxShadow: hasAudit ? `0 0 40px -8px ${scoreCol}66` : 'none' }}>
              <div style={{ width: 116, height: 116, borderRadius: '50%', background: '#060d14', display: 'grid', placeItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: SAIRA, fontWeight: 200, fontSize: 80, lineHeight: 1, color: hasAudit ? scoreCol : '#56788c', textShadow: hasAudit ? `0 0 22px ${scoreCol}66` : 'none' }}>{hasAudit ? score : '—'}</div>
                  <div style={{ fontFamily: MONO, fontSize: 13, letterSpacing: '.2em', color: '#49677a', marginTop: 3 }}>/ 100</div>
                </div>
              </div>
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontFamily: MONO, fontSize: 13, letterSpacing: '.16em', color: '#7fa9bd' }}>{winLabel}{campName ? ` · ${campName.toUpperCase()}` : ''}</span>
              <span style={{ padding: '3px 10px', borderRadius: 999, border: `1px solid ${isLive ? 'rgba(93,242,180,.5)' : 'rgba(125,232,255,.3)'}`, color: isLive ? '#5df2b4' : '#7fa9bd', fontFamily: MONO, fontSize: 13, letterSpacing: '.18em' }}>{hasAudit ? (isLive ? 'LIVE · GRAPH API' : 'SNAPSHOT · MCP') : 'NO AUDIT'}</span>
            </div>
          </div>
          {!hasAudit && noAudit && (
            <div style={{ position: 'absolute', left: 14, right: 14, bottom: 14, padding: '10px 12px', borderRadius: 10, border: '1px dashed rgba(255,195,107,.4)', background: 'rgba(255,195,107,.04)' }}>
              <span style={{ fontFamily: SAIRA, fontWeight: 300, fontSize: 22, color: '#ffc36b' }}>No audit yet for {acct?.name || 'this account'} — REFRESH pulls one from the Meta Graph API.</span>
            </div>
          )}
        </div>

        {/* score breakdown */}
        <div style={brk.sl.s}><div style={scanline} />{brk.head}
          {(audit?.scoreParts || []).map(p => (
            <div key={p.label} style={{ marginTop: 9 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontFamily: SAIRA, fontWeight: 300, fontSize: 23, color: '#c4d8e4' }}>{p.label.toUpperCase()} <span style={{ color: '#49677a', fontSize: 18 }}>· {p.weight}%</span></span>
                <span style={{ fontFamily: MONO, fontSize: 20, color: scC(p.score) }}>{Math.round(p.score)}{p.note && <span style={{ color: '#49677a', fontSize: 16 }}> · {p.note}</span>}</span>
              </div>
              <div style={{ height: 2, marginTop: 4, width: `${p.score}%`, background: `linear-gradient(90deg,${scC(p.score)},rgba(125,232,255,.05))`, boxShadow: `0 0 8px ${scC(p.score)}44`, transition: 'width .5s' }} />
            </div>
          ))}
        </div>

        {/* KPI tiles */}
        <div style={kpi.sl.s}><div style={scanline} />{kpi.head}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginTop: 8 }}>
            {kpis.map(k => (
              <div key={k[0]} style={{ borderRadius: 10, border: '1px solid rgba(125,232,255,.14)', background: 'rgba(8,18,28,.4)', padding: '10px 12px' }}>
                <div style={{ fontFamily: MONO, fontSize: 13, letterSpacing: '.2em', color: '#49677a' }}>{k[0]}</div>
                <div style={{ fontFamily: SAIRA, fontWeight: 200, fontSize: 42, color: '#aef0ff', textShadow: '0 0 14px rgba(0,240,255,.3)', margin: '2px 0' }}>{k[1]}</div>
                <div style={{ fontFamily: MONO, fontSize: 17, color: k[2] === '—' ? '#56788c' : k[3] ? '#5df2b4' : '#ff7d8a' }}>{k[2]}</div>
              </div>
            ))}
            {!kpis.length && <div style={{ gridColumn: '1/-1', fontFamily: SAIRA, fontWeight: 300, fontSize: 23, color: '#56788c' }}>Refresh to pull KPIs from Meta.</div>}
          </div>
        </div>

        {/* conversions */}
        <div style={conv.sl.s}><div style={scanline} />{conv.head}
          <div style={{ overflowY: 'auto', maxHeight: GEOM.conv[3] - 50 }}>
            {(audit?.conversions || []).map(c => {
              const primary = audit?.primaryConversion?.id === c.id;
              return (
                <div key={c.id} style={{ marginTop: 11, padding: '10px 12px', borderRadius: 10, border: `1px solid rgba(125,232,255,${primary ? '.35' : '.14'})`, background: primary ? 'rgba(0,240,255,.05)' : 'rgba(8,18,28,.4)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: SAIRA, fontWeight: 300, fontSize: 23, color: '#e9f9ff' }}>{c.name}</span>
                    {primary && <span style={{ padding: '2px 8px', borderRadius: 999, border: '1px solid rgba(93,242,180,.5)', color: '#5df2b4', fontFamily: MONO, fontSize: 13, letterSpacing: '.16em' }}>PRIMARY</span>}
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 18, color: '#aef0ff', marginTop: 3 }}>{c.count} <span style={{ color: '#56788c' }}>AT {fmtMoney(c.costPer, cur)}</span></div>
                </div>
              );
            })}
            {!(audit?.conversions?.length) && <div style={{ marginTop: 14, fontFamily: SAIRA, fontWeight: 300, fontSize: 22, color: '#56788c' }}>No conversions tracked this window.</div>}
          </div>
        </div>

        {/* smart insights */}
        <div style={ins.sl.s}><div style={scanline} />{ins.head}
          <div style={{ overflowY: 'auto', maxHeight: GEOM.ins[3] - 50 }}>
            {(bundle?.insights || []).map((i, n) => (
              <div key={n} style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', flex: 'none', alignSelf: 'center', background: SEV[i.severity]?.[0] || '#9ef0ff', boxShadow: `0 0 10px ${SEV[i.severity]?.[0] || '#9ef0ff'}` }} />
                <span style={{ fontFamily: SAIRA, fontWeight: 300, fontSize: 23, lineHeight: 1.55, color: '#c4d8e4' }}><b style={{ fontWeight: 400, color: '#e9f9ff' }}>{i.title}</b> — {i.detail}</span>
              </div>
            ))}
            {!(bundle?.insights?.length) && <div style={{ marginTop: 14, fontFamily: SAIRA, fontWeight: 300, fontSize: 22, color: '#56788c' }}>Insights arrive with the live breakdowns bundle.</div>}
          </div>
        </div>

        {/* findings */}
        <div style={fnd.sl.s}><div style={scanline} />{fnd.head}
          <div style={{ overflowY: 'auto', maxHeight: GEOM.fnd[3] - 50 }}>
            {(audit?.findings || []).map((f, i) => {
              const sv = SEV[f.severity] || SEV.info;
              return (
                <div key={i} style={{ marginTop: 10, padding: '9px 12px', borderRadius: 10, borderLeft: `2px solid ${sv[0]}`, background: 'rgba(8,18,28,.45)' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ display: 'grid', placeItems: 'center', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${sv[0]}`, color: sv[0], fontFamily: MONO, fontSize: 16, flex: 'none' }}>{sv[1]}</span>
                    <span style={{ fontFamily: SAIRA, fontWeight: 400, fontSize: 23, color: '#e9f9ff' }}>{f.title}</span>
                  </div>
                  <div style={{ fontFamily: SAIRA, fontWeight: 300, fontSize: 21, lineHeight: 1.55, color: '#9fb8c6', marginTop: 3, paddingLeft: 24 }}>{f.detail}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* recommendations */}
        <div style={rec.sl.s}><div style={scanline} />{rec.head}
          <div style={{ overflowY: 'auto', maxHeight: GEOM.rec[3] - 50 }}>
            {(audit?.recommendations || []).map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'flex-start' }}>
                <span style={{ padding: '2px 8px', borderRadius: 999, border: `1px solid ${PRI[r.priority]}77`, color: PRI[r.priority], fontFamily: MONO, fontSize: 13, letterSpacing: '.14em', flex: 'none', marginTop: 2 }}>{r.priority.toUpperCase()}</span>
                <span style={{ fontFamily: SAIRA, fontWeight: 300, fontSize: 23, lineHeight: 1.55, color: '#c4d8e4' }}><b style={{ fontWeight: 400, color: '#e9f9ff' }}>{r.title}</b> — {r.detail}</span>
              </div>
            ))}
            {hasAudit && !(audit?.recommendations.length) && <div style={{ marginTop: 16, fontFamily: SAIRA, fontWeight: 300, fontSize: 23, color: '#5df2b4' }}>No actions — account looks healthy.</div>}
          </div>
        </div>

        {/* benchmarks */}
        <div style={bmk.sl.s}><div style={scanline} />{bmk.head}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginTop: 8 }}>
            {(audit?.benchmarks || []).map(b => {
              const good = b.verdict === 'inline' || (b.better === 'higher' ? b.verdict === 'above' : b.verdict === 'below');
              return (
                <div key={b.metric} style={{ borderRadius: 10, border: '1px solid rgba(125,232,255,.14)', background: 'rgba(8,18,28,.4)', padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: MONO, fontSize: 13, letterSpacing: '.2em', color: '#49677a' }}>{b.metric.toUpperCase()}</span>
                    <span style={{ padding: '2px 8px', borderRadius: 999, border: `1px solid ${good ? 'rgba(93,242,180,.5)' : 'rgba(255,125,138,.5)'}`, color: good ? '#5df2b4' : '#ff7d8a', fontFamily: MONO, fontSize: 13, letterSpacing: '.14em' }}>{b.verdict === 'inline' ? 'NEAR' : b.verdict.toUpperCase()}</span>
                  </div>
                  <div style={{ fontFamily: SAIRA, fontWeight: 200, fontSize: 38, color: '#aef0ff', margin: '3px 0 1px' }}>{b.metric === 'CTR' ? `${b.you}%` : `${b.unit}${b.you}`}</div>
                  <div style={{ fontFamily: MONO, fontSize: 14, color: '#49677a' }}>REF {b.metric === 'CTR' ? `${b.reference}%` : `${b.unit}${b.reference}`}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* notes */}
        <div style={note.sl.s}><div style={scanline} />{note.head}
          <div style={{ overflowY: 'auto', maxHeight: GEOM.note[3] - 66 }}>
            {(audit?.notes.length ? audit.notes : ['Refresh to generate the first audit — notes arrive with it.']).map((n, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginTop: 7 }}>
                <span style={{ color: '#3d4654' }}>•</span>
                <span style={{ fontFamily: SAIRA, fontWeight: 300, fontSize: 21, lineHeight: 1.55, color: '#9fb8c6' }}>{n}</span>
              </div>
            ))}
          </div>
          <div style={{ position: 'absolute', left: 14, bottom: 10, fontFamily: MONO, fontSize: 13, letterSpacing: '.16em', color: '#3d4654' }}>
            {audit ? `GENERATED ${new Date(audit.generatedAt).toISOString().slice(0, 16).replace('T', ' ')} · ${isLive ? 'META GRAPH API' : 'MCP SNAPSHOT'} · AUDITSEQ GUARD ACTIVE` : ''}
          </div>
        </div>

        {/* TRENDS / AUDIENCE / DELIVERY — three slabs, content per tab */}
        {(['trends', 'audience', 'delivery'] as Tab[]).includes(tab) && charts.map(([title, id], ci) => {
          const rows = tab === 'audience' ? audRows[ci][2] : tab === 'delivery' ? delRows[ci][2] : [];
          const t = tab === 'trends' ? title : tab === 'audience' ? audRows[ci][0] : delRows[ci][0];
          const { sl, head } = slabHead(t, id);
          return (
            <div key={id} style={sl.s}><div style={scanline} />{head}
              {tab === 'trends' ? (
                bundle?.timeseries.length ? (
                  <svg viewBox="0 0 320 90" style={{ width: '100%', height: 200, marginTop: 12 }} preserveAspectRatio="none">
                    <path d={chartPath(charts[ci][2])} fill="none" stroke="#9ef0ff" strokeWidth="1.4" style={{ filter: 'drop-shadow(0 0 6px rgba(0,240,255,.5))' }} />
                  </svg>
                ) : <div style={{ marginTop: 16, fontFamily: SAIRA, fontWeight: 300, fontSize: 22, color: '#56788c' }}>Timeseries arrives with the live bundle.</div>
              ) : (
                <div style={{ overflowY: 'auto', maxHeight: GEOM[id][3] - 66 }}>
                  {barRows([...rows])}
                  {!rows.length && <div style={{ marginTop: 16, fontFamily: SAIRA, fontWeight: 300, fontSize: 22, color: '#56788c' }}>Breakdown arrives with the live bundle.</div>}
                </div>
              )}
              <div style={{ position: 'absolute', left: 14, bottom: 10, fontFamily: MONO, fontSize: 13, letterSpacing: '.16em', color: '#3d4654' }}>BUNDLE · {winLabel}</div>
            </div>
          );
        })}

        {/* CAMPAIGNS — three entity tables */}
        {tab === 'campaigns' && audit && (<>
          {entityTable('Campaigns', audit.campaigns, 't1')}
          {entityTable('Ad sets', audit.adsets, 't2')}
          {entityTable('Ads', audit.ads, 't3')}
        </>)}

        {/* TRACKER */}
        <div style={trk.sl.s}><div style={scanline} />{trk.head}
          <div style={{ overflowY: 'auto', maxHeight: GEOM.trk[3] - 56, marginTop: 8 }}>
            {(() => {
              const rows: any[] = tracker?.rows || tracker?.days || [];
              if (!rows.length) return <div style={{ marginTop: 12, fontFamily: SAIRA, fontWeight: 300, fontSize: 22, color: '#56788c' }}>No tracker rows stored for {new Date().toISOString().slice(0, 7)} — Refresh writes them.</div>;
              const byCamp: Record<string, any[]> = {};
              rows.forEach((r: any) => { const k = r.campaignName || r.campaign || 'ACCOUNT'; (byCamp[k] = byCamp[k] || []).push(r); });
              const mx = Math.max(...rows.map((r: any) => r.spend || 0), 0.0001);
              return Object.entries(byCamp).filter(([k]) => campaign === '__ALL__' || k === campName).map(([k, rs]) => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                  <span style={{ flex: '0 0 220px', fontFamily: SAIRA, fontWeight: 300, fontSize: 22, color: '#e9f9ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k}</span>
                  <div style={{ flex: 1, display: 'flex', gap: 3 }}>
                    {Array.from({ length: 31 }, (_, d) => {
                      const cell = rs.find((r: any) => new Date(r.date).getDate() === d + 1);
                      const v = cell ? (cell.spend || 0) / mx : 0;
                      return <div key={d} title={cell ? `${cell.date} · ${fmtMoney(cell.spend || 0, cur)}` : ''} style={{ flex: 1, height: 26, borderRadius: 3, background: `rgba(158,240,255,${0.04 + v * .4})`, boxShadow: v > .8 ? '0 0 8px rgba(0,240,255,.35)' : 'none' }} />;
                    })}
                  </div>
                  <span style={{ flex: '0 0 80px', textAlign: 'right', fontFamily: MONO, fontSize: 18, color: '#aef0ff' }}>{fmtMoney(rs.reduce((s: number, r: any) => s + (r.spend || 0), 0), cur)}</span>
                </div>
              ));
            })()}
          </div>
        </div>

        {/* ASSISTANT — the real gateway-backed ads assistant */}
        <div style={{ ...ast.sl.s, padding: 0 }}><div style={scanline} />
          <div style={{ padding: 14, paddingBottom: 0 }}>{ast.head}</div>
          <div style={{ height: GEOM.ast[3] - 54, overflowY: 'auto', padding: '0 14px 14px' }}>
            {selected && <AdsAssistant accountId={selected} accountName={acct?.name || selected} currency={cur} audit={audit} rangeLabel={winLabel} />}
          </div>
        </div>
      </div>

      {/* acceptance checklist panel (persists bcc.ads.checklist) */}
      <div style={{ position: 'fixed', left: 360, right: 360, bottom: 60, borderRadius: 16, border: '1px solid rgba(125,232,255,.4)', background: 'linear-gradient(170deg,rgba(40,140,180,.12),rgba(4,10,18,.96) 55%)', boxShadow: '0 40px 100px -24px rgba(0,0,0,.9),0 0 70px -16px rgba(0,240,255,.35)', padding: 16, zIndex: 88, opacity: checkOpen ? 1 : 0, transform: checkOpen ? 'translateY(0)' : 'translateY(24px)', pointerEvents: checkOpen ? 'auto' : 'none', visibility: checkOpen ? 'visible' : 'hidden', transition: 'all .35s cubic-bezier(.25,.9,.3,1)' }}>
        <div style={{ fontFamily: MONO, fontSize: 13, letterSpacing: '.22em', color: '#49677a', marginBottom: 10 }}>ACCEPTANCE · HANDOFF §1.6 · TICKS PERSIST</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
          {CKS.map((c, i) => {
            const on = !!check[i];
            return (
              <div key={i} onClick={() => setCheck(prev => { const n = { ...prev, [i]: !prev[i] }; try { localStorage.setItem('bcc.ads.checklist', JSON.stringify(n)); } catch { } return n; })}
                style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                <span style={{ display: 'grid', placeItems: 'center', width: 16, height: 16, borderRadius: 5, border: `1px solid ${on ? 'rgba(93,242,180,.7)' : 'rgba(125,232,255,.3)'}`, color: '#5df2b4', fontSize: 18, flex: 'none', background: on ? 'rgba(93,242,180,.08)' : 'transparent', transition: 'all .2s' }}>{on ? '✓' : ''}</span>
                <span style={{ fontFamily: SAIRA, fontWeight: 300, fontSize: 22, color: on ? '#bfe9d8' : '#9fb8c6' }}>{c}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
